import AvatarFallback from '@avanta/shared/assets/avatar.svg';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { UserType } from '@avanta/shared/constants/users';
import { cn } from '@avanta/shared/utils/cn';
import { getUserDisplayName } from '@avanta/shared/utils/users';
import { Headset, LogOut, Settings } from 'lucide-react';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, NavLink, useLocation, useNavigate } from 'react-router';
import Logo from '@/assets/logo-itae.svg';
import { ROUTES } from 'constants/routes';
import { AuthService } from 'services/auth';
import { useUserStore } from 'stores/user';
import { getSidebarRoutes } from 'utils/nav';

const NAV_ITEM_CLASS_NAME =
  'relative flex h-10 items-center gap-3 px-4 py-2 text-sm font-medium transition-colors';
const ACTIVE_NAV_CLASS_NAME =
  'bg-denim-400 text-teal-300 before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-teal-300';

const isPatientAppointmentPath = (pathname: string): boolean =>
  Boolean(matchPath({ path: ROUTES.PATIENT_APPOINTMENT, end: true }, pathname));

type AppointmentLocationState = {
  from?: string;
};

export const Sidebar = (): ReactElement => {
  const user = useUserStore(state => state.user);
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const userDisplayName = getUserDisplayName(user);
  const userRoleLabel =
    user?.type === UserType.ENTERPRISE
      ? 'AdministratorsPage.Table.role.options.ADMIN'
      : `AdministratorsPage.Table.role.options.${user?.role}`;

  const userAvatarSrc =
    user?.imageCircular || user?.imageSquare || user?.imageURL || AvatarFallback;
  const sidebarRoutes = getSidebarRoutes(user?.type as UserType, user?.role);
  const locationState = state as AppointmentLocationState | null;
  const cameFromSessions =
    isPatientAppointmentPath(pathname) && locationState?.from === ROUTES.APPOINTMENTS;

  const isNavRouteActive = (href: string, isActive: boolean): boolean => {
    if (cameFromSessions) {
      if (href === ROUTES.APPOINTMENTS) return true;
      if (href === ROUTES.PATIENTS) return false;
    }

    return isActive;
  };

  return (
    <aside className="bg-denim-500 w-52 shrink-0 self-stretch">
      <header className="sticky top-0 flex h-dvh flex-col overflow-y-auto pb-4">
        <div className="flex flex-1 flex-col">
          <div className="mt-3 mb-4 flex justify-center">
            <img src={Logo} alt="itae" className="h-14 w-auto object-contain" />
          </div>
          <nav className="flex flex-col gap-3">
            {sidebarRoutes.map(sidebarRoute => {
              if (!sidebarRoute.href) {
                return (
                  <div
                    key={sidebarRoute.label}
                    className={cn(NAV_ITEM_CLASS_NAME, 'text-teal-500 cursor-default')}
                  >
                    <sidebarRoute.icon className="size-4 shrink-0" aria-hidden />
                    <span className="truncate">{t(`Nav.${sidebarRoute.label}`)}</span>
                  </div>
                );
              }

              const href = sidebarRoute.href;

              return (
                <NavLink
                  key={href}
                  to={href}
                  end={href === ROUTES.HOME}
                  className={({ isActive }) => {
                    const active = isNavRouteActive(href, isActive);

                    return cn(
                      NAV_ITEM_CLASS_NAME,
                      active ? ACTIVE_NAV_CLASS_NAME : 'text-teal-500',
                      !active && 'hover:bg-denim-400/60 hover:text-teal-300'
                    );
                  }}
                >
                  <sidebarRoute.icon className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{t(`Nav.${sidebarRoute.label}`)}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="mt-auto flex flex-col">
            <div className="flex items-center gap-1 px-2">
              <div className="inline-flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full">
                <img
                  src={userAvatarSrc}
                  alt={userDisplayName || t('Nav.profile')}
                  className="size-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="text-denim-200 truncate text-sm leading-4.5 font-medium">
                  {userDisplayName}
                </p>
                <p className="text-denim-300 truncate text-xs leading-4 font-normal">
                  {t(userRoleLabel, { ns: LOCIZE_NS.INSURANCE })}
                </p>
              </div>
            </div>
            <div className="border-denim-400 mt-2 mx-4 flex flex-col items-start overflow-hidden rounded-2xl border">
              <button
                type="button"
                onClick={() => navigate(ROUTES.PROFILE)}
                className="text-denim-200 flex h-10 w-full items-center gap-2 self-stretch px-4 py-2 text-left text-sm leading-4 font-normal transition-colors hover:bg-denim-400/60"
              >
                <Settings className="text-denim-200 size-4 shrink-0" aria-hidden />
                <span>{t('Nav.profile')}</span>
              </button>
              <button
                type="button"
                onClick={() => AuthService.logout()}
                className="border-denim-400 text-denim-200 flex h-10 w-full items-center gap-2 self-stretch border-t px-4 py-2 text-left text-sm leading-4.5 font-normal transition-colors hover:bg-denim-400/60"
              >
                <LogOut className="text-denim-200 size-4 shrink-0" aria-hidden />
                <span>{t('Nav.logout')}</span>
              </button>
            </div>
            <button
              type="button"
              className="bg-denim-400 text-denim-100 mt-6 mx-4 inline-flex h-9 items-center justify-center gap-2 rounded-full px-4 text-sm font-medium shadow-box-xs"
            >
              <Headset className="size-4" aria-hidden />
              <span>{t('Nav.support')}</span>
            </button>
          </div>
        </div>
      </header>
    </aside>
  );
};
